import React from 'react'
import { PhysicalPopUpCTAButtons, PopUpCTA, ProjectPopUpCTAButtons } from '../[slug]/components/pop-up-cta'
import {
  Enum_Exhibition_Exhibitionstage,
  ExhibitionPageDetailsFragment,
  PopUpCtaProjectFragment
} from '@/gql/generated/graphql'
import { STATUS_REFRESH_INTERVAL_SECONDS, useProjectStatus } from '@/hooks/use-project-status'
import {
  iskraShopifyProduct,
  iskraTitle,
  larsShopifyProduct,
  zachTitle,
  zachShopifyProduct,
  larsTitle
} from './display'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { ShopifyProduct } from '@utils/shopify'
import { ValidStatusType } from '@utils/project'
import { TimerText } from '../[slug]/components/release-timer'

export const GroupExhibitionQ42024PopUpCTA = ({
  exhibition
}: {
  exhibition: ExhibitionPageDetailsFragment
}): React.ReactElement => {
  const exhibitionStage = exhibition.ExhibitionStage

  const iskraDigital = exhibition.Collections?.[0]?.Collection?.Project ?? undefined
  const iskraPhysical = exhibition.Collections?.[0]?.AdditionalProjects?.[0]?.Project ?? undefined
  const larsDigital = exhibition.Collections?.[1]?.Collection?.Project ?? undefined
  const larsPhysical = exhibition.Collections?.[1]?.AdditionalProjects?.[0]?.Project ?? undefined
  const zachDigital = exhibition.Collections?.[2]?.Collection?.Project ?? undefined
  const zachPhysical = exhibition.Collections?.[2]?.AdditionalProjects?.[0]?.Project ?? undefined
  let projectToDisplay: PopUpCtaProjectFragment | undefined = iskraPhysical
  let isPhysical = false
  let physicalIndex = 0

  const physicalsInfo: { title: string; imgSrc: string; shopifyProduct: ShopifyProduct }[] = [
    {
      title: 'INK (fragrances)',
      imgSrc: getPrefixedLinkPath('/images/poetics/heroCTAs/heroCTA-Fragrance.jpg'),
      shopifyProduct: iskraShopifyProduct
    },
    {
      title: 'while true (fabric)',
      imgSrc: getPrefixedLinkPath('/images/poetics/heroCTAs/heroCTA-Curtains.jpg'),
      shopifyProduct: larsShopifyProduct
    },
    {
      title: `${zachTitle} (sculpture)`,
      imgSrc: getPrefixedLinkPath('/images/poetics/heroCTAs/heroCTA-Sculpture.jpg'),
      shopifyProduct: zachShopifyProduct
    }
  ]
  let title = ''

  switch (exhibitionStage) {
    case Enum_Exhibition_Exhibitionstage.Project_1:
      projectToDisplay = iskraPhysical
      isPhysical = true
      break
    case Enum_Exhibition_Exhibitionstage.Project_2:
      projectToDisplay = iskraDigital
      isPhysical = false
      title = iskraTitle
      break
    case Enum_Exhibition_Exhibitionstage.Project_3:
      projectToDisplay = larsPhysical
      physicalIndex = 1
      isPhysical = true
      break
    case Enum_Exhibition_Exhibitionstage.Project_4:
      projectToDisplay = larsDigital
      isPhysical = false
      title = larsTitle
      break
    case Enum_Exhibition_Exhibitionstage.Project_5:
      projectToDisplay = zachPhysical
      physicalIndex = 2
      isPhysical = true
      break
    case Enum_Exhibition_Exhibitionstage.Project_6:
      projectToDisplay = zachDigital
      isPhysical = false
      title = zachTitle
      break
    case Enum_Exhibition_Exhibitionstage.Complete:
    default:
      projectToDisplay = undefined
      break
  }
  const { status, refetchStatus } = useProjectStatus(projectToDisplay)

  React.useEffect(() => {
    const interval = setInterval(() => {
      refetchStatus()
    }, STATUS_REFRESH_INTERVAL_SECONDS * 1000)

    if (status === ValidStatusType.complete) {
      clearInterval(interval)
      return
    }

    return () => clearInterval(interval)
  }, [refetchStatus, status])

  if (!projectToDisplay) {
    return <></>
  }

  let imgSrc = projectToDisplay.featured_token?.[0]?.media_url ?? ''
  let footer = <ProjectPopUpCTAButtons project={projectToDisplay} status={status} />
  const timer = <TimerText project={projectToDisplay} />
  if (isPhysical) {
    const physicalData = physicalsInfo[physicalIndex]
    title = physicalData.title
    imgSrc = physicalData.imgSrc
    footer = (
      <PhysicalPopUpCTAButtons
        project={projectToDisplay}
        status={status}
        shopifyProduct={physicalData.shopifyProduct}
      />
    )
  }

  return <PopUpCTA title={title} imgSrc={imgSrc} timer={timer} footer={footer} status={status} />
}
